// @flow

import React, { useCallback, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { compose, withHandlers, withProps, withState } from "recompose";

import { any, applySpec, isEmpty, prop } from "ramda";

import {
  Modal,
  OkButton,
  RemoveIcon,
  withModal,
  IconButton,
} from "@quantum/ui";
import { isBlank } from "@quantum/utils";
import { Translate, withI18n } from "@quantum/i18n";

import { currentUser } from "../../../state";

import { IoIosClose } from "react-icons/io";

import "./send-mail-modal.css";
import { openNotification } from "../ui";

type Props = {
  onSendMail: Function,
};

type EnhancedProps = {
  cco: string,
  isSubmitDisabled: boolean,
  isSenderBlank: boolean,
  body: string,
  from: string,
  subject: string,
  show: boolean,
  to: string,
  onChangeCco: Function,
  onChangeBody: Function,
  onChangeSubject: Function,
  onChangeTo: Function,
  onHide: Function,
  onSubmit: Function,
  translate: Function,
};

const initialValidationObj = {
  to: true,
  cco: true,
};

const errorMessage = {
  to: "",
  cco: "",
};

const MAX_QUANTITY_TO = 30;
const MAX_QUANTITY_CCO = 10;

const inputHandler = setter => ({ [setter]: setValue }) => ({
  target: { value },
}) => setValue(value);

const enhance = compose(
  connect(
    applySpec({
      senderEmail: compose(prop("email"), currentUser),
      senderName: compose(prop("name"), currentUser),
    })
  ),

  withI18n,

  withModal("sendMail"),

  withState("to", "setTo", ""),
  withState("cco", "setCco", prop("senderEmail")),
  withState("subject", "setSubject", ({ translate }) =>
    translate("Email.Msg.AssuntoPadrao")
  ),
  withState("body", "setBody", ({ translate }) =>
    translate("Email.Msg.ConteudoPadrao")
  ),

  withProps(({ body, subject, to, senderEmail, senderName }) => ({
    from: `${senderName} (${senderEmail})`,
    isSenderBlank: isBlank(senderEmail),
    isSubmitDisabled: any(isEmpty, [body, subject, to.trim()]),
  })),

  withHandlers({
    onChangeCco: inputHandler("setCco"),
    onChangeBody: inputHandler("setBody"),
    onChangeSubject: inputHandler("setSubject"),
    onChangeTo: inputHandler("setTo"),

    onSubmit: ({ cco, body, subject, to, onSendMail, onHide }) => (
      event,
      validateForm
    ) => {
      event.preventDefault();
      if (validateForm()) {
        onSendMail({ cco, body, subject, to });
        onHide();
      }
    },
  })
);

const SendMailModal = ({
  cco,
  isSubmitDisabled,
  isSenderBlank,
  body,
  from,
  subject,
  show,
  to,
  onChangeCco,
  onChangeBody,
  onChangeSubject,
  onChangeTo,
  onHide,
  onSubmit,
  translate,
}: Props & EnhancedProps) => {
  const dispatch = useDispatch();

  const openErrorNotification = useCallback(
    () => dispatch(openNotification("email-no-sender")),
    [dispatch]
  );

  useEffect(() => {
    if (show && isSenderBlank) {
      openErrorNotification();
      onHide();
    }
  }, [show, isSenderBlank, openErrorNotification, onHide]);

  const [validation, setValidation] = useState("formValidation");
  if (validation === "formValidation") {
    setValidation(initialValidationObj);
  }

  const validateAddressFormat = email => {
    if (email.trim() !== "") {
      var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (email.indexOf(";") !== -1) {
        var emailSplit = email.trim().split(";");
        for (var i = 0; i < emailSplit.length; i++) {
          if (!re.test(emailSplit[i].trim())) {
            return false;
          }
        }
        return true;
      }
      return re.test(email.trim());
    }
    return true;
  };

  const validateAddressQuantity = (email, maxQuantity) => {
    if (email.indexOf(";") !== -1) {
      var emailSplit = email.trim().split(";");
      return emailSplit.length <= maxQuantity;
    }
    return true;
  };

  const validadeTo = () => {
    if (!validateAddressFormat(to)) {
      errorMessage.to = "envioEmail.validacao.emailInvalido";
    } else if (!validateAddressQuantity(to, MAX_QUANTITY_TO)) {
      errorMessage.to = "envioEmail.validacao.limiteDeEmail";
    } else {
      return true;
    }

    return false;
  };

  const validadeCco = () => {
    if (!validateAddressFormat(cco)) {
      errorMessage.cco = "envioEmail.validacao.emailInvalido";
    } else if (!validateAddressQuantity(cco, MAX_QUANTITY_CCO)) {
      errorMessage.cco = "envioEmail.validacao.limiteDeEmail";
    } else {
      return true;
    }

    return false;
  };

  const onSubmitValidation = () => {
    const validationResult = {
      to: validadeTo(),
      cco: validadeCco(),
    };
    setValidation(validationResult);
    return Object.values(validationResult).every(element => element === true);
  };

  if (show && isSenderBlank) {
    return null;
  }

  return (
    <Modal
      className="SendMailModal"
      header={onHide => (
        <div className="SendMailModal__header">
          <h1 className="SendMailModal__title">
            <Translate>Comum.EnviarPorEmail</Translate>
          </h1>
          <div className="SendMailModal__close" onClick={onHide}>
            <RemoveIcon />
          </div>
        </div>
      )}
      show={show}
      onHide={onHide}
    >
      <form
        className="SendMailModal__form"
        onSubmit={event => onSubmit(event, onSubmitValidation)}
      >
        <div className="SendMailModal__form-section">
          <label className="SendMailModal__label">
            <Translate>Email.De</Translate>:
          </label>
          <input readOnly className="SendMailModal__input" value={from} />
        </div>

        <div className="SendMailModal__form-section">
          <label className="SendMailModal__label">
            <Translate>Email.Para</Translate>:
          </label>
          <input
            className="SendMailModal__input"
            placeholder={translate("Email.Msg.ParaPlaceholder")}
            onChange={onChangeTo}
            value={to}
          />
          {!validation.to && (
            <div className="SendMailModal__validacao-texto--erro">
              <IconButton
                className="SendMailModal__botao-validacao"
                icon={<IoIosClose />}
                type="button"
              >
                <strong>
                  <Translate>{errorMessage.to}</Translate>
                </strong>
              </IconButton>
            </div>
          )}
        </div>

        <div className="SendMailModal__form-section">
          <label className="SendMailModal__label">
            <Translate>Email.Cco</Translate>:
          </label>
          <input
            className="SendMailModal__input"
            onChange={onChangeCco}
            value={cco}
          />
          {!validation.cco && (
            <div className="SendMailModal__validacao-texto--erro">
              <IconButton
                className="SendMailModal__botao-validacao"
                icon={<IoIosClose />}
                type="button"
              >
                <strong>
                  <Translate>{errorMessage.cco}</Translate>
                </strong>
              </IconButton>
            </div>
          )}
        </div>

        <div className="SendMailModal__form-section">
          <label className="SendMailModal__label">
            <Translate>Email.Assunto</Translate>:
          </label>
          <input
            className="SendMailModal__input"
            onChange={onChangeSubject}
            value={subject}
          />
        </div>

        <div className="SendMailModal__form-section">
          <label className="SendMailModal__label">
            <Translate>Email.Mensagem</Translate>:
          </label>
          <textarea
            className="SendMailModal__input SendMailModal__input-area"
            onChange={onChangeBody}
            value={body}
          />
        </div>

        <div className="SendMailModal__form-section">
          <OkButton
            className="SendMailModal__submit"
            disabled={isSubmitDisabled}
          >
            <Translate>Email.Enviar</Translate>
          </OkButton>
        </div>
      </form>
    </Modal>
  );
};

export default enhance(SendMailModal);
